// Single-select insight options are either an ORDERED scale (e.g. Strongly for →
// Strongly against) or an unordered set of CATEGORIES (e.g. Pro/Anti/Neutral).
// Ordered scales get a sequential single-hue ramp light↔dark — the correct
// encoding for magnitude/order. Categories get a fixed-order set of distinct
// hues instead — a gradient would imply a magnitude relationship the data
// doesn't have (see DonutInsightCard, which picks one of the two by
// `insight.scale`). Both ramps are generated for any option count, so a Core
// question gaining an option needs no code change (SFOA-35 AC #4).

type Rgb = [number, number, number];

// Endpoints of the ramp: darkest = first (highest-affinity) option, lightest = last.
const DARK: Rgb = [13, 71, 161]; // #0D47A1
const LIGHT: Rgb = [187, 222, 251]; // #BBDEFB

const toHex = ([r, g, b]: Rgb): string =>
  `#${[r, g, b].map(channel => channel.toString(16).padStart(2, '0')).join('')}`;

const lerp = (from: number, to: number, t: number): number => Math.round(from + (to - from) * t);

/**
 * `count` sequential blues, dark→light. One option → the darkest end so a
 * single-option donut still reads intentionally rather than washed out.
 */
export const sequentialBlues = (count: number): string[] => {
  if (count <= 0) return [];
  if (count === 1) return [toHex(DARK)];
  return Array.from({ length: count }, (_, index) => {
    const t = index / (count - 1);
    return toHex([lerp(DARK[0], LIGHT[0], t), lerp(DARK[1], LIGHT[1], t), lerp(DARK[2], LIGHT[2], t)] as Rgb);
  });
};

// Fixed hue order, validated (dataviz skill's reference palette): worst adjacent
// CVD ΔE 9.1, worst adjacent normal-vision ΔE 19.6 on the #fcfcfb chart surface.
// Never reordered/cycled — color must follow the entity, not its rank.
const CATEGORICAL_HUES = [
  '#2a78d6', // blue
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#e87ba4', // magenta
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
];

// Beyond the 8 validated hues, fold into a shared muted "Other" swatch rather
// than generating/cycling an unvalidated color.
const OTHER_HUE = '#898781';

/** `count` fixed-order categorical hues for an unordered option set. */
export const categoricalHues = (count: number): string[] => {
  if (count <= 0) return [];
  return Array.from({ length: count }, (_, index) => CATEGORICAL_HUES[index] ?? OTHER_HUE);
};
